import Handlebars from "handlebars";
import type { OrderLineItem, ReturnRequest } from "../types";

/**
 * Data a merchant custom message (`shop.preferences.userPortal.*`) can read.
 * Same two roots as the legacy widget (`apps/return-widget/src/utils/handlebars.ts`),
 * so a message written for it renders identically here.
 */
export type PortalTemplateContext = {
  returnRequest?: ReturnRequest | null;
  availableItems?: OrderLineItem[] | null;
};

// A helper receives null outside the end of the flow, and the Handlebars
// options object when called without argument (`{{hasExchange}}`): both read
// as no line item, as in the legacy widget.
const isReturnRequest = (arg: unknown): arg is ReturnRequest =>
  typeof arg === "object" &&
  arg !== null &&
  "line_items" in arg &&
  Array.isArray(arg.line_items);

const returnedLineItems = (arg: unknown) =>
  isReturnRequest(arg) ? arg.line_items : [];

const orderLineItems = (arg: unknown): OrderLineItem[] =>
  Array.isArray(arg) ? arg : [];

const hasResolution =
  (...resolutions: string[]) =>
  (returnRequest: unknown) =>
    returnedLineItems(returnRequest).some((lineItem) =>
      resolutions.some((resolution) => resolution === lineItem.resolution),
    );

// The last argument of a variadic helper is the Handlebars options object.
const operands = (args: unknown[]) => args.slice(0, -1);

const HELPERS: Record<string, Handlebars.HelperDelegate> = {
  hasExchange: hasResolution("exchange", "exchange-credit"),
  hasRefund: hasResolution("refund"),
  hasDiscount: hasResolution("discount"),
  hasProductTag: (returnRequest: unknown, tag: string) =>
    returnedLineItems(returnRequest).some((lineItem) =>
      lineItem.product.tags?.includes(tag),
    ),
  getReturnLineItem: (returnRequest: unknown) =>
    returnedLineItems(returnRequest).filter(
      (lineItem) => lineItem.quantity > 0,
    ),
  isReturningAllItems: (returnRequest: unknown) =>
    isReturnRequest(returnRequest) &&
    returnRequest.line_items.every(
      (lineItem) => lineItem.quantity === lineItem.quantity_bought,
    ),
  hasLineItemCustomAttributeKeyValue: (
    availableItems: unknown,
    key: string,
    value: string,
  ) =>
    orderLineItems(availableItems).some((lineItem) =>
      lineItem.customAttributes.some(
        (attribute) => attribute.key === key && attribute.value === value,
      ),
    ),
  hasAvailableItemProductTag: (availableItems: unknown, tag: string) =>
    orderLineItems(availableItems).some((lineItem) =>
      lineItem.product.tags.includes(tag),
    ),
  eq: (a: unknown, b: unknown) => a === b,
  gt: (a: number, b: number) => a > b,
  lt: (a: number, b: number) => a < b,
  or: (...args: unknown[]) => operands(args).some(Boolean),
  and: (...args: unknown[]) => operands(args).every(Boolean),
};

const handlebars = Handlebars.create();
handlebars.registerHelper(HELPERS);

// The back-office rich text editor stores quotes and operators as entities,
// which would break helper arguments such as `"merchant-carrier"`.
const HTML_ENTITIES: Record<string, string> = {
  "&quot;": '"',
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
};

const decodeEntities = (template: string) =>
  template.replace(/&(?:quot|amp|lt|gt);/g, (entity) => HTML_ENTITIES[entity]);

/**
 * Renders a merchant custom message. Returns null when the message has no
 * visible text once rendered (a conditional block whose condition is false),
 * or when the merchant's template does not parse.
 */
export function compilePortalTemplate(
  template: string,
  context: PortalTemplateContext,
): string | null {
  try {
    const html = handlebars.compile(decodeEntities(template))({
      returnRequest: context.returnRequest ?? null,
      availableItems: context.availableItems ?? null,
    });
    return html.replace(/<[^>]*>/g, "").trim() ? html : null;
  } catch {
    return null;
  }
}
